import { useEffect, useState } from 'react';
import { formatDateTime, parseResidents } from '../lib/format';
import type { Complaint, ComplaintStatus, Report } from '../types';
import { Spinner, EmptyState, StatusBadge } from './ui';
import { api, buildQuery } from '../lib/api';
import ReportAttachments from './ReportAttachments';

const STATUSES: ComplaintStatus[] = ['open', 'in_review', 'resolved', 'dismissed'];

type Tab = 'personal' | 'residents';

interface Props {
  /** Show the personal (staff) complaints tab. Admin: true. Manager: false. */
  personal?: boolean;
  /** Allow changing complaint status. Only admins can. */
  canUpdateStatus?: boolean;
}

export default function ComplaintsAdmin({ personal = true, canUpdateStatus = true }: Props) {
  const [tab, setTab] = useState<Tab>('personal');
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [residents, setResidents] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let active = true;

    const load = async () => {
      const loadPersonal = personal ? api.get<Complaint[]>('/complaints') : Promise.resolve<Complaint[]>([]);
      const loadResidents = api.get<Report[]>(`/reports${buildQuery({ report_type: 'residents_complaint' })}`);
      try {
        const [c, r] = await Promise.all([loadPersonal, loadResidents]);
        if (!active) return;
        if (tab === 'personal' || !personal) setComplaints(c);
        if (tab === 'residents' || !personal) setResidents(r);
      } catch {
        // ignore
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [tab, personal]);

  const updateStatus = async (id: number, status: ComplaintStatus) => {
    try {
      const updated = await api.patch<Complaint>(`/complaints/${id}/status`, { status });
      setComplaints((prev) => prev.map((c) => (c.id === id ? updated : c)));
    } catch {
      // ignore
    }
  };

  if (!personal) {
    return (
      <div>
        <h1 className="text-2xl font-bold mb-6">Residents Complaints</h1>
        {loading ? (
          <Spinner label="Loading complaints..." />
        ) : residents.length === 0 ? (
          <EmptyState message="No residents complaints yet." />
        ) : (
          <div className="grid gap-4">
            {residents.map((r) => {
              const residentEntries = parseResidents(r.resident_name, r.unit);
              return (
              <div key={r.id} className="bg-white p-5 rounded-lg shadow-lg">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <h3 className="font-semibold">{r.heading || "Residents' Complaint"}</h3>
                  <StatusBadge status={r.report_type} />
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  {r.user_name} · {residentEntries.length > 0 ? residentEntries.map((re) => `Resident: ${re.name}${re.unit ? ` (${re.unit})` : ''}`).join(', ') + ' · ' : ''}
                  {formatDateTime(r.created_at)}
                </p>
                <p className="text-sm text-gray-600 whitespace-pre-line mb-3">{r.content}</p>
                <ReportAttachments attachments={r.attachments} />
              </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Complaints</h1>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setTab('personal')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${tab === 'personal' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Staff Complaints
        </button>
        <button
          onClick={() => setTab('residents')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${tab === 'residents' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Residents Complaints
        </button>
      </div>

      {loading ? (
        <Spinner label="Loading complaints..." />
      ) : tab === 'personal' ? (
        complaints.length === 0 ? (
          <EmptyState message="No staff complaints yet." />
        ) : (
          <div className="grid gap-4">
            {complaints.map((c) => (
              <div key={c.id} className="bg-white p-5 rounded-lg shadow-lg">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="font-semibold">{c.subject}</h3>
                      <StatusBadge status={c.status} />
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      {c.user_name} · {formatDateTime(c.created_at)}
                    </p>
                    <p className="text-sm text-gray-600 whitespace-pre-line">{c.description}</p>
                  </div>
                  {canUpdateStatus && (
                    <div className="shrink-0">
                      <label className="block text-xs text-gray-500 mb-1">Status</label>
                      <select
                        value={c.status}
                        onChange={(e) => updateStatus(c.id, e.target.value as ComplaintStatus)}
                        className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : residents.length === 0 ? (
        <EmptyState message="No residents complaints yet." />
      ) : (
        <div className="grid gap-4">
          {residents.map((r) => {
            const residentEntries = parseResidents(r.resident_name, r.unit);
            return (
            <div key={r.id} className="bg-white p-5 rounded-lg shadow-lg">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h3 className="font-semibold">{r.heading || "Residents' Complaint"}</h3>
                <StatusBadge status={r.report_type} />
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {r.user_name} · {residentEntries.length > 0 ? residentEntries.map((re) => `Resident: ${re.name}${re.unit ? ` (${re.unit})` : ''}`).join(', ') + ' · ' : ''}
                {formatDateTime(r.created_at)}
              </p>
              <p className="text-sm text-gray-600 whitespace-pre-line mb-3">{r.content}</p>
              <ReportAttachments attachments={r.attachments} />
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
